import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCompletionCelebrationImagesReady,
  STAR_SRC,
  STRIDER_SRC,
} from '@/components/CompletionCelebration/completionCelebrationAssets';

const FLY_IN_MS = 600;
const PAUSE_MS = 800;
const FLY_OUT_MS = 600;
const TOTAL_MS = FLY_IN_MS + PAUSE_MS + FLY_OUT_MS;
const AUTO_DISMISS_MS = TOTAL_MS + 200;

const STRIDER_CX = 50;
const STRIDER_CY = 50;
const BURST_DELAY_MS = FLY_IN_MS + 40;
const STAR_ANIM_MS = TOTAL_MS - BURST_DELAY_MS;

const STAR_BURST_COUNT = 20;
const RADIUS_MIN_VW = 6;
const RADIUS_MAX_VW = 46;

const STARS = Array.from({ length: STAR_BURST_COUNT }, (_, i) => {
  const baseAngleDeg = (360 / STAR_BURST_COUNT) * i;
  const jitterDeg = ((i * 53) % 17) - 8;
  const angle = (baseAngleDeg + jitterDeg) * (Math.PI / 180);
  const radius = RADIUS_MIN_VW + ((i * 0.41) % 1) * (RADIUS_MAX_VW - RADIUS_MIN_VW);
  const finalX = STRIDER_CX + Math.cos(angle) * radius;
  const finalY = STRIDER_CY + Math.sin(angle) * radius;
  const kind = ((((i * 5) % 2) + 1) as keyof typeof STAR_SRC);
  return {
    kind,
    finalX,
    finalY,
    sx: STRIDER_CX - finalX,
    sy: STRIDER_CY - finalY,
    delayMs: BURST_DELAY_MS,
  };
});

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

export interface CompletionCelebrationProps {
  open: boolean;
  onDismiss?: () => void;
  /** When true, overlay does not auto-dismiss (design/tests). Motion still runs unless reduced-motion. */
  freeze?: boolean;
}

const CompletionCelebration: React.FC<CompletionCelebrationProps> = ({ open, onDismiss, freeze = false }) => {
  const reduced = usePrefersReducedMotion();
  const dismissBtnRef = useRef<HTMLButtonElement>(null);
  const [imagesReady, setImagesReady] = useState(false);

  const stars = useMemo(() => (open && imagesReady ? STARS : []), [open, imagesReady]);

  useEffect(() => {
    if (!open) {
      setImagesReady(false);
      return;
    }

    let cancelled = false;
    void getCompletionCelebrationImagesReady().then(() => {
      if (!cancelled) setImagesReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss?.();
      if (e.key === 'Tab') {
        e.preventDefault();
        dismissBtnRef.current?.focus();
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !imagesReady) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => dismissBtnRef.current?.focus(), 0);
    const dismissTimer = freeze ? null : window.setTimeout(() => onDismiss?.(), AUTO_DISMISS_MS);

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      if (dismissTimer) window.clearTimeout(dismissTimer);
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open, imagesReady, onDismiss, freeze, handleKeyDown]);

  if (!open || !imagesReady) return null;

  const reducedMotion = reduced;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Task complete"
      onClick={onDismiss}
      className={`completion-celebration completion-celebration--images-ready${
        reducedMotion ? ' completion-celebration--reduced' : ''
      }`}
    >
      <button
        ref={dismissBtnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss?.();
        }}
        aria-label="Dismiss celebration"
        className="completion-celebration__dismiss"
      >
        ×
      </button>

      {stars.map((s, i) => {
        const style: React.CSSProperties & Record<'--sx' | '--sy', string> = {
          left: `${s.finalX}%`,
          top: `${s.finalY}%`,
          '--sx': `${s.sx}vw`,
          '--sy': `${s.sy}vh`,
        };
        if (reducedMotion) {
          style.transform = 'translate(-50%, -50%)';
          style.opacity = 1;
        } else {
          style.transform = 'translate(-50%, -50%) translate(var(--sx), var(--sy)) scale(0.3)';
          style.opacity = 0;
          style.animation = `completion-celeb-star-emit ${STAR_ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${s.delayMs}ms both`;
        }

        return (
          <img
            key={i}
            src={STAR_SRC[s.kind]}
            alt=""
            aria-hidden={true}
            loading="eager"
            decoding="sync"
            className={`completion-celebration__star${reducedMotion ? ' completion-celebration__star--reduced' : ''}`}
            style={style}
          />
        );
      })}

      <div className="completion-celebration__flight">
        <div className="completion-celebration__strider-wrap">
          <div className="completion-celebration__strider-rotate">
            <img
              src={STRIDER_SRC}
              alt=""
              aria-hidden={true}
              draggable={false}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className="completion-celebration__strider-img"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionCelebration;
